"use client"

import { useState, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useRouter } from "next/navigation"
import Header from "@/components/header"
import Footer from "@/components/footer"
import { StarfieldBackground } from "@/components/starfield-background"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { LivenessCheck } from "@/components/liveness-check"
import {
    UserCheck,
    Users,
    Building2,
    ArrowRight,
    ArrowLeft,
    CheckCircle2,
    Upload,
    FileText,
    Camera,
    MapPin,
    Loader2,
    BadgeCheck,
    Home,
    Copy,
    Check,
    Search,
    Mail,
    Link as LinkIcon,
    Clock,
    User,
    AlertCircle
} from "lucide-react"
import Link from "next/link"
import { cn } from "@/lib/utils"
import {
    validateEmail,
    validateTelegram,
    validateWebsite,
    validateFullName,
    validateProjectName,
    validateDescription,
    validateTeamMemberEmail,
    FormValidationErrors
} from "@/lib/validation"

const plans = [
    {
        id: "individual",
        name: "Individual KYC",
        price: "$99",
        description: "For single project founders",
        icon: UserCheck,
        features: ["1 team member verification", "KYC Verified badge", "24-48 hour processing", "Valid for 1 year"]
    },
    {
        id: "team",
        name: "Team KYC",
        price: "$249",
        description: "For project teams",
        icon: Users,
        features: ["Up to 5 team members", "KYC Verified badge", "Priority processing", "Valid for 1 year", "Team profile page"],
        popular: true
    },
    {
        id: "enterprise",
        name: "Enterprise KYC",
        price: "$499",
        description: "For registered companies",
        icon: Building2,
        features: ["Unlimited team members", "Company verification", "Express 12-hour processing", "Valid for 2 years", "Dedicated support"]
    }
]

const stepTitles = [
    "Select Plan",
    "Your Information",
    "Document Upload",
    "Liveness Check",
    "Review & Submit"
]

export function KYCApplyContent() {
    const router = useRouter()
    const [step, setStep] = useState(1)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [livenessCompleted, setLivenessCompleted] = useState(false)
    const [serialNumber, setSerialNumber] = useState("")
    const [copied, setCopied] = useState(false)
    const [formData, setFormData] = useState({
        plan: "",
        name: "",
        email: "",
        projectName: "",
        telegram: "",
        website: "",
        description: "",
        teamSize: "1",
        teamMembers: [{ name: "", email: "", livenessStatus: "pending" as "pending" | "sent" | "completed" }]
    })
    const [teamLivenessLinks, setTeamLivenessLinks] = useState<Record<number, string>>({})
    const [sendingLinkFor, setSendingLinkFor] = useState<number | null>(null)
    const [fieldErrors, setFieldErrors] = useState<FormValidationErrors>({})
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

    // Validation on blur
    const handleBlur = useCallback((field: string) => {
        setTouchedFields(prev => new Set(prev).add(field))

        const errors: FormValidationErrors = {}

        switch (field) {
            case 'name':
                const nameResult = validateFullName(formData.name)
                if (!nameResult.valid) errors.name = nameResult.error!
                break
            case 'email':
                const emailResult = validateEmail(formData.email)
                if (!emailResult.valid) errors.email = emailResult.error!
                break
            case 'projectName':
                const projectResult = validateProjectName(formData.projectName)
                if (!projectResult.valid) errors.projectName = projectResult.error!
                break
            case 'telegram':
                if (formData.telegram) {
                    const telegramResult = validateTelegram(formData.telegram)
                    if (!telegramResult.valid) errors.telegram = telegramResult.error!
                }
                break
            case 'website':
                if (formData.website) {
                    const websiteResult = validateWebsite(formData.website)
                    if (!websiteResult.valid) errors.website = websiteResult.error!
                }
                break
            case 'description':
                if (formData.description) {
                    const descResult = validateDescription(formData.description)
                    if (!descResult.valid) errors.description = descResult.error!
                }
                break
        }

        // Handle team member validation
        if (field.startsWith('teamMember')) {
            const match = field.match(/teamMember(\d+)(Name|Email)/)
            if (match) {
                const idx = parseInt(match[1])
                const type = match[2]
                const member = formData.teamMembers[idx]
                if (member) {
                    if (type === 'Name' && member.name) {
                        const result = validateFullName(member.name)
                        if (!result.valid) errors[field] = result.error!
                    } else if (type === 'Email' && member.email) {
                        const result = validateTeamMemberEmail(member.email, formData.email)
                        if (!result.valid) errors[field] = result.error!
                    }
                }
            }
        }

        setFieldErrors(prev => {
            const updated = { ...prev }
            Object.keys(errors).length > 0
                ? Object.assign(updated, errors)
                : delete updated[field]
            return updated
        })
    }, [formData])

    // Check if form is valid for current step
    const isStepValid = useCallback(() => {
        if (step === 2) {
            const nameValid = validateFullName(formData.name).valid
            const emailValid = validateEmail(formData.email).valid
            const projectValid = validateProjectName(formData.projectName).valid
            const telegramValid = !formData.telegram || validateTelegram(formData.telegram).valid
            const websiteValid = !formData.website || validateWebsite(formData.website).valid

            // Validate team members for team plan
            let teamMembersValid = true
            if (formData.plan === 'team') {
                teamMembersValid = formData.teamMembers.every((m, idx) => {
                    if (!m.name && !m.email) return true // Empty row is ok
                    return validateFullName(m.name).valid && validateTeamMemberEmail(m.email, formData.email).valid
                })
            }

            return nameValid && emailValid && projectValid && telegramValid && websiteValid && teamMembersValid
        }
        return true
    }, [formData, step])

    // Error message component
    const FieldError = ({ field }: { field: string }) => {
        const error = fieldErrors[field]
        const touched = touchedFields.has(field)
        if (!error || !touched) return null
        return (
            <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
                <AlertCircle className="h-3 w-3" />
                {error}
            </p>
        )
    }

    const totalSteps = 5

    const nextStep = () => {
        if (step < totalSteps) {
            setStep(step + 1)
            window.scrollTo({ top: 0, behavior: "smooth" })
        }
    }

    const prevStep = () => {
        if (step > 1) {
            setStep(step - 1)
            window.scrollTo({ top: 0, behavior: "smooth" })
        }
    }

    const handleSubmit = async () => {
        setIsSubmitting(true)
        try {
            const response = await fetch("/api/kyc/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    livenessCompleted
                })
            })

            const data = await response.json()

            if (response.ok && data.serial) {
                setSerialNumber(data.serial)
                setIsSuccess(true)
                window.scrollTo({ top: 0, behavior: "smooth" })
            }
        } catch (error) {
            console.error("Failed to submit KYC application:", error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const canProceed = () => {
        switch (step) {
            case 1:
                return formData.plan !== ""
            case 2:
                return formData.name && formData.email && formData.projectName && isStepValid() && Object.keys(fieldErrors).length === 0
            case 3:
                return true // Document upload is optional
            case 4:
                return true // Liveness can be skipped
            case 5:
                return true
            default:
                return false
        }
    }

    const selectedPlan = plans.find(p => p.id === formData.plan)

    const copySerial = () => {
        if (serialNumber) {
            navigator.clipboard.writeText(serialNumber)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    if (isSuccess) {
        return (
            <div className="min-h-screen flex flex-col relative">
                <StarfieldBackground />
                <Header />
                <main className="flex-1 relative z-10 flex items-center justify-center py-20">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center p-8 max-w-lg"
                    >
                        <div className="h-24 w-24 rounded-full bg-[#7ed957] flex items-center justify-center mx-auto mb-6">
                            <BadgeCheck className="h-14 w-14 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold mb-4">Application Submitted!</h1>
                        <p className="text-muted-foreground mb-6">
                            Thank you for your KYC application. Our team will review your
                            submission and contact you within 24-48 hours.
                        </p>

                        {/* Serial Number Display */}
                        <div className="bg-muted/50 rounded-xl p-6 mb-8">
                            <p className="text-sm text-muted-foreground mb-2">Your Application Serial Number</p>
                            <div className="flex items-center justify-center gap-3">
                                <span className="font-mono text-xl font-bold tracking-wider">
                                    {serialNumber}
                                </span>
                                <button
                                    onClick={copySerial}
                                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                                >
                                    {copied ? (
                                        <Check className="h-5 w-5 text-[#7ed957]" />
                                    ) : (
                                        <Copy className="h-5 w-5 text-muted-foreground" />
                                    )}
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">
                                Save this number to check your application status
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button asChild className="bg-[#7ed957] hover:bg-[#6bc448]">
                                <Link href="/status">
                                    <Search className="h-4 w-4 mr-2" />
                                    Check Status
                                </Link>
                            </Button>
                            <Button asChild variant="outline">
                                <Link href="/kyc">
                                    <Home className="h-4 w-4 mr-2" />
                                    Back to KYC
                                </Link>
                            </Button>
                        </div>
                    </motion.div>
                </main>
                <Footer />
            </div>
        )
    }

    return (
        <div className="min-h-screen flex flex-col relative pt-20">
            <StarfieldBackground />
            <Header />
            <main className="flex-1 relative z-10 py-12 md:py-20">
                <div className="container max-w-4xl">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Link
                            href="/kyc"
                            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to KYC
                        </Link>
                        <h1 className="text-3xl font-bold mb-2">KYC Application</h1>
                        <p className="text-muted-foreground">
                            Complete the verification process to build trust with investors
                        </p>
                    </div>

                    {/* Progress Steps */}
                    <div className="flex items-center justify-center mb-12 w-fit mx-auto">
                        {stepTitles.map((title, index) => {
                            const stepNum = index + 1
                            const isActive = step === stepNum
                            const isCompleted = step > stepNum
                            return (
                                <div key={stepNum} className="flex items-center">
                                    <div className="flex flex-col items-center shrink-0">
                                        <motion.div
                                            className={cn(
                                                "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all",
                                                isCompleted
                                                    ? "bg-[#7ed957] text-white"
                                                    : isActive
                                                        ? "bg-[#7ed957] text-white ring-4 ring-[#7ed957]/30"
                                                        : "bg-muted text-muted-foreground"
                                            )}
                                            animate={{ scale: isActive ? 1.1 : 1 }}
                                        >
                                            {isCompleted ? <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" /> : stepNum}
                                        </motion.div>
                                        <span className={cn(
                                            "text-xs mt-2 text-center hidden sm:block",
                                            isActive ? "text-foreground font-medium" : "text-muted-foreground"
                                        )}>
                                            {title}
                                        </span>
                                    </div>
                                    {stepNum < totalSteps && (
                                        <div className={cn(
                                            "w-8 sm:w-16 h-0.5 sm:h-1 mx-1 sm:mx-2 rounded transition-all",
                                            isCompleted ? "bg-[#7ed957]" : "bg-muted"
                                        )} />
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* Step Content */}
                    <Card className="border-border/40">
                        <CardContent className="p-6 md:p-8">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={step}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Step 1: Plan Selection */}
                                    {step === 1 && (
                                        <div className="space-y-6">
                                            <div className="text-center mb-8">
                                                <h2 className="text-2xl font-bold mb-2">Select Your Plan</h2>
                                                <p className="text-muted-foreground">
                                                    Choose the verification plan that fits your needs
                                                </p>
                                            </div>
                                            <div className="grid gap-4 md:grid-cols-3">
                                                {plans.map((plan) => {
                                                    const Icon = plan.icon
                                                    return (
                                                        <motion.div
                                                            key={plan.id}
                                                            onClick={() => setFormData({ ...formData, plan: plan.id })}
                                                            className={cn(
                                                                "relative p-6 rounded-xl border-2 cursor-pointer transition-all",
                                                                formData.plan === plan.id
                                                                    ? "border-[#7ed957] bg-[#7ed957]/5"
                                                                    : "border-border hover:border-[#7ed957]/50"
                                                            )}
                                                            whileHover={{ y: -5 }}
                                                            whileTap={{ scale: 0.98 }}
                                                        >
                                                            {plan.popular && (
                                                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 bg-[#7ed957] text-white rounded-full">
                                                                    Popular
                                                                </span>
                                                            )}
                                                            <div className="text-center">
                                                                <div className="h-14 w-14 mx-auto rounded-xl bg-[#7ed957]/10 flex items-center justify-center mb-4">
                                                                    <Icon className="h-7 w-7 text-[#7ed957]" />
                                                                </div>
                                                                <h3 className="font-bold mb-1">{plan.name}</h3>
                                                                <div className="text-2xl font-bold text-[#7ed957] mb-2">{plan.price}</div>
                                                                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                                                                <ul className="text-xs text-left space-y-2">
                                                                    {plan.features.map((feature, idx) => (
                                                                        <li key={idx} className="flex items-center gap-2">
                                                                            <CheckCircle2 className="h-3 w-3 text-[#7ed957]" />
                                                                            {feature}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </motion.div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2: Information */}
                                    {step === 2 && (
                                        <div className="space-y-6 max-w-xl mx-auto">
                                            <div className="text-center mb-8">
                                                <h2 className="text-2xl font-bold mb-2">Your Information</h2>
                                                <p className="text-muted-foreground">
                                                    Tell us about yourself and your project
                                                </p>
                                            </div>
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="name">Full Name *</Label>
                                                    <Input
                                                        id="name"
                                                        placeholder="John Doe"
                                                        value={formData.name}
                                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                        onBlur={() => handleBlur('name')}
                                                        className={cn(fieldErrors.name && touchedFields.has('name') && "border-red-500 focus-visible:ring-red-500")}
                                                    />
                                                    <FieldError field="name" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="email">Email Address *</Label>
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        placeholder="john@example.com"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                        onBlur={() => handleBlur('email')}
                                                        className={cn(fieldErrors.email && touchedFields.has('email') && "border-red-500 focus-visible:ring-red-500")}
                                                    />
                                                    <FieldError field="email" />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="projectName">Project Name *</Label>
                                                <Input
                                                    id="projectName"
                                                    placeholder="Your project name"
                                                    value={formData.projectName}
                                                    onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                                                    onBlur={() => handleBlur('projectName')}
                                                    className={cn(fieldErrors.projectName && touchedFields.has('projectName') && "border-red-500 focus-visible:ring-red-500")}
                                                />
                                                <FieldError field="projectName" />
                                            </div>
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="telegram">Telegram Username</Label>
                                                    <Input
                                                        id="telegram"
                                                        placeholder="@username"
                                                        value={formData.telegram}
                                                        onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                                                        onBlur={() => handleBlur('telegram')}
                                                        className={cn(fieldErrors.telegram && touchedFields.has('telegram') && "border-red-500 focus-visible:ring-red-500")}
                                                    />
                                                    <FieldError field="telegram" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="website">Project Website</Label>
                                                    <Input
                                                        id="website"
                                                        placeholder="https://yourproject.com"
                                                        value={formData.website}
                                                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                                        onBlur={() => handleBlur('website')}
                                                        className={cn(fieldErrors.website && touchedFields.has('website') && "border-red-500 focus-visible:ring-red-500")}
                                                    />
                                                    <FieldError field="website" />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="description">Project Description</Label>
                                                <Textarea
                                                    id="description"
                                                    placeholder="Tell us about your project..."
                                                    value={formData.description}
                                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                    onBlur={() => handleBlur('description')}
                                                    rows={4}
                                                />
                                                <FieldError field="description" />
                                            </div>

                                            {/* Team Members for Team KYC */}
                                            {formData.plan === "team" && (
                                                <div className="space-y-4 pt-4 border-t">
                                                    <div className="flex justify-between items-center">
                                                        <Label>Team Members (up to 5)</Label>
                                                        {formData.teamMembers.length < 5 && (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setFormData({
                                                                    ...formData,
                                                                    teamMembers: [...formData.teamMembers, { name: "", email: "", livenessStatus: "pending" as const }]
                                                                })}
                                                            >
                                                                + Add Member
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {formData.teamMembers.map((member, idx) => (
                                                        <div key={idx} className="grid gap-4 sm:grid-cols-2 p-4 bg-muted/30 rounded-lg relative">
                                                            <div className="absolute -top-2 -left-2 h-6 w-6 rounded-full bg-[#7ed957] text-white text-xs flex items-center justify-center font-medium">
                                                                {idx + 1}
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label>Member Name</Label>
                                                                <Input
                                                                    placeholder="Team member name"
                                                                    value={member.name}
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.teamMembers]
                                                                        updated[idx].name = e.target.value
                                                                        setFormData({ ...formData, teamMembers: updated })
                                                                    }}
                                                                    onBlur={() => handleBlur(`teamMember${idx}Name`)}
                                                                    className={cn(fieldErrors[`teamMember${idx}Name`] && touchedFields.has(`teamMember${idx}Name`) && "border-red-500 focus-visible:ring-red-500")}
                                                                />
                                                                <FieldError field={`teamMember${idx}Name`} />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label>Member Email</Label>
                                                                <Input
                                                                    type="email"
                                                                    placeholder="member@example.com"
                                                                    value={member.email}
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.teamMembers]
                                                                        updated[idx].email = e.target.value
                                                                        setFormData({ ...formData, teamMembers: updated })
                                                                    }}
                                                                    onBlur={() => handleBlur(`teamMember${idx}Email`)}
                                                                    className={cn(fieldErrors[`teamMember${idx}Email`] && touchedFields.has(`teamMember${idx}Email`) && "border-red-500 focus-visible:ring-red-500")}
                                                                />
                                                                <FieldError field={`teamMember${idx}Email`} />
                                                            </div>
                                                            {formData.teamMembers.length > 1 && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-destructive text-white hover:bg-destructive/90"
                                                                    onClick={() => {
                                                                        const updated = formData.teamMembers.filter((_, i) => i !== idx)
                                                                        setFormData({ ...formData, teamMembers: updated })
                                                                    }}
                                                                >
                                                                    ×
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Step 3: Document Upload */}
                                    {step === 3 && (
                                        <div className="space-y-8 max-w-2xl mx-auto">
                                            {/* Founder Documents */}
                                            <div>
                                                <div className="text-center mb-6">
                                                    <h2 className="text-2xl font-bold mb-2">Founder Documents</h2>
                                                    <p className="text-muted-foreground">
                                                        Upload verification documents for the project founder
                                                    </p>
                                                </div>

                                                <div className="grid gap-4">
                                                    {[
                                                        { icon: FileText, title: "Government ID", desc: "Passport, Driver's License, or National ID" },
                                                        { icon: Camera, title: "Selfie with ID", desc: "Clear photo holding your ID" },
                                                        { icon: MapPin, title: "Proof of Address", desc: "Utility bill or bank statement (last 3 months)" }
                                                    ].map((doc, index) => (
                                                        <div
                                                            key={index}
                                                            className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-[#7ed957]/50 transition-colors cursor-pointer"
                                                        >
                                                            <div className="h-12 w-12 mx-auto rounded-xl bg-muted flex items-center justify-center mb-3">
                                                                <doc.icon className="h-6 w-6 text-muted-foreground" />
                                                            </div>
                                                            <h4 className="font-medium mb-1">{doc.title}</h4>
                                                            <p className="text-xs text-muted-foreground mb-3">{doc.desc}</p>
                                                            <Button variant="outline" size="sm">
                                                                <Upload className="h-4 w-4 mr-2" />
                                                                Upload File
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Team Member Documents */}
                                            {formData.plan === "team" && formData.teamMembers.length > 0 && (
                                                <div className="pt-8 border-t">
                                                    <div className="text-center mb-6">
                                                        <h2 className="text-xl font-bold mb-2">Team Member Documents</h2>
                                                        <p className="text-muted-foreground text-sm">
                                                            Upload documents for each team member (or have them submit via their liveness link)
                                                        </p>
                                                    </div>

                                                    <div className="space-y-6">
                                                        {formData.teamMembers.map((member, idx) => (
                                                            <div key={idx} className="bg-muted/30 rounded-xl p-6">
                                                                <div className="flex items-center gap-3 mb-4">
                                                                    <div className="h-8 w-8 rounded-full bg-[#7ed957] text-white text-sm flex items-center justify-center font-medium">
                                                                        {idx + 1}
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="font-medium">{member.name || `Team Member ${idx + 1}`}</h4>
                                                                        <p className="text-xs text-muted-foreground">{member.email || "No email provided"}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="grid gap-3 sm:grid-cols-3">
                                                                    {["ID Document", "Selfie with ID", "Proof of Address"].map((docName, docIdx) => (
                                                                        <div
                                                                            key={docIdx}
                                                                            className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-[#7ed957]/50 transition-colors cursor-pointer"
                                                                        >
                                                                            <p className="text-xs font-medium mb-2">{docName}</p>
                                                                            <Button variant="outline" size="sm" className="text-xs">
                                                                                <Upload className="h-3 w-3 mr-1" />
                                                                                Upload
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <p className="text-xs text-center text-muted-foreground">
                                                Files are encrypted and stored securely. You can skip this step and submit documents via email.
                                            </p>
                                        </div>
                                    )}

                                    {/* Step 4: Liveness Check */}
                                    {step === 4 && (
                                        <div className="max-w-2xl mx-auto">
                                            {/* Founder Liveness - Mandatory */}
                                            <div className="mb-8">
                                                <div className="text-center mb-6">
                                                    <h2 className="text-2xl font-bold mb-2">Founder Liveness Verification</h2>
                                                    <p className="text-muted-foreground">
                                                        This step is <span className="text-[#7ed957] font-medium">mandatory</span> for the project founder
                                                    </p>
                                                </div>

                                                {livenessCompleted ? (
                                                    <div className="bg-[#7ed957]/10 border border-[#7ed957]/20 rounded-xl p-6 flex items-center gap-4">
                                                        <div className="h-12 w-12 rounded-full bg-[#7ed957] flex items-center justify-center">
                                                            <CheckCircle2 className="h-6 w-6 text-white" />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-bold text-[#7ed957]">Liveness Check Completed</h4>
                                                            <p className="text-sm text-muted-foreground">Your identity has been verified successfully</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <LivenessCheck
                                                        onSuccess={() => {
                                                            setLivenessCompleted(true)
                                                        }}
                                                        onSkip={() => {
                                                            // Founder cannot skip - show message
                                                        }}
                                                    />
                                                )}
                                            </div>

                                            {/* Team Member Liveness Links */}
                                            {formData.plan === "team" && formData.teamMembers.length > 0 && (
                                                <div className="pt-8 border-t">
                                                    <div className="text-center mb-6">
                                                        <h2 className="text-xl font-bold mb-2">Team Member Liveness</h2>
                                                        <p className="text-muted-foreground text-sm">
                                                            Send unique verification links to your team members (optional)
                                                        </p>
                                                    </div>

                                                    <div className="space-y-3">
                                                        {formData.teamMembers.map((member, idx) => (
                                                            <div key={idx} className="bg-muted/30 rounded-xl p-4 flex items-center justify-between gap-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                                                        <User className="h-5 w-5 text-muted-foreground" />
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="font-medium text-sm">{member.name || `Member ${idx + 1}`}</h4>
                                                                        <p className="text-xs text-muted-foreground">{member.email || "No email"}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {member.livenessStatus === "completed" ? (
                                                                        <div className="flex items-center gap-2 text-[#7ed957]">
                                                                            <CheckCircle2 className="h-4 w-4" />
                                                                            <span className="text-sm font-medium">Verified</span>
                                                                        </div>
                                                                    ) : member.livenessStatus === "sent" || teamLivenessLinks[idx] ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                                <Clock className="h-3 w-3" />
                                                                                Link Sent
                                                                            </span>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-8"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(teamLivenessLinks[idx] || "")
                                                                                }}
                                                                            >
                                                                                <Copy className="h-3 w-3" />
                                                                            </Button>
                                                                        </div>
                                                                    ) : (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            disabled={!member.email || sendingLinkFor === idx}
                                                                            onClick={async () => {
                                                                                if (!member.email) return
                                                                                setSendingLinkFor(idx)
                                                                                // Simulate generating and sending link
                                                                                await new Promise(r => setTimeout(r, 1000))
                                                                                const mockLink = `${window.location.origin}/liveness/team-${Date.now()}-${idx}`
                                                                                setTeamLivenessLinks(prev => ({ ...prev, [idx]: mockLink }))
                                                                                const updated = [...formData.teamMembers]
                                                                                updated[idx] = { ...updated[idx], livenessStatus: "sent" }
                                                                                setFormData({ ...formData, teamMembers: updated })
                                                                                setSendingLinkFor(null)
                                                                            }}
                                                                        >
                                                                            {sendingLinkFor === idx ? (
                                                                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                                            ) : (
                                                                                <Mail className="h-3 w-3 mr-1" />
                                                                            )}
                                                                            Send Link
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <p className="text-xs text-center text-muted-foreground mt-4">
                                                        Links are valid for 48 hours. Team members can complete verification at their convenience.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Navigation for step 4 */}
                                            {livenessCompleted && (
                                                <div className="flex justify-center mt-8">
                                                    <Button
                                                        onClick={nextStep}
                                                        className="bg-[#7ed957] hover:bg-[#6bc448]"
                                                    >
                                                        Continue to Review
                                                        <ArrowRight className="h-4 w-4 ml-2" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Step 5: Review */}
                                    {step === 5 && (
                                        <div className="space-y-6 max-w-xl mx-auto">
                                            <div className="text-center mb-8">
                                                <h2 className="text-2xl font-bold mb-2">Review Your Application</h2>
                                                <p className="text-muted-foreground">
                                                    Please verify your information before submitting
                                                </p>
                                            </div>

                                            <div className="bg-muted/50 rounded-xl p-6 space-y-4">
                                                <div className="flex justify-between items-center pb-4 border-b border-border">
                                                    <span className="text-muted-foreground">Selected Plan</span>
                                                    <span className="font-bold text-[#7ed957]">{selectedPlan?.name} ({selectedPlan?.price})</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Name</span>
                                                    <span className="font-medium">{formData.name}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Email</span>
                                                    <span className="font-medium">{formData.email}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Project</span>
                                                    <span className="font-medium">{formData.projectName}</span>
                                                </div>
                                                {formData.telegram && (
                                                    <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Telegram</span>
                                                        <span className="font-medium">{formData.telegram}</span>
                                                    </div>
                                                )}
                                                {formData.website && (
                                                    <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Website</span>
                                                        <span className="font-medium">{formData.website}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center pt-4 border-t border-border">
                                                    <span className="text-muted-foreground">Liveness Check</span>
                                                    <span className={cn(
                                                        "font-medium flex items-center gap-2",
                                                        livenessCompleted ? "text-[#7ed957]" : "text-yellow-500"
                                                    )}>
                                                        {livenessCompleted ? (
                                                            <>
                                                                <CheckCircle2 className="h-4 w-4" />
                                                                Completed
                                                            </>
                                                        ) : (
                                                            "Skipped"
                                                        )}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="bg-[#7ed957]/10 border border-[#7ed957]/20 rounded-xl p-4">
                                                <h4 className="font-semibold mb-2 flex items-center gap-2">
                                                    <CheckCircle2 className="h-4 w-4 text-[#7ed957]" />
                                                    What happens next?
                                                </h4>
                                                <ul className="text-sm text-muted-foreground space-y-1">
                                                    <li>• Our team will review your application within 24-48 hours</li>
                                                    <li>• You'll receive an email with document upload instructions</li>
                                                    <li>• Once verified, your KYC badge will be activated</li>
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            {/* Navigation */}
                            {step !== 4 && (
                                <div className="flex justify-between mt-8 pt-6 border-t">
                                    <Button
                                        variant="ghost"
                                        onClick={prevStep}
                                        disabled={step === 1}
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back
                                    </Button>
                                    {step < totalSteps ? (
                                        <Button
                                            onClick={nextStep}
                                            disabled={!canProceed()}
                                            className="bg-[#7ed957] hover:bg-[#6bc448]"
                                        >
                                            Continue
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                            className="bg-[#7ed957] hover:bg-[#6bc448]"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Application
                                                    <ArrowRight className="h-4 w-4 ml-2" />
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* SEO explainer — below the content, above footer */}
            <section className="container max-w-3xl mx-auto pb-16">
                <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
                    <h2 className="text-base font-semibold text-foreground">What Is KYC Verification?</h2>
                    <p>
                        KYC (Know Your Customer) verification confirms the identity of project founders and team
                        members. Completing KYC shows investors that the people behind a project are real and
                        accountable, which builds trust and helps attract more participation.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>ID verification</strong> — upload a government-issued photo ID for identity checks.</li>
                        <li><strong>Selfie check</strong> — take a live photo to match against your ID document.</li>
                        <li><strong>Trust badge</strong> — verified projects display a green KYC badge on their presale page.</li>
                        <li><strong>Turnaround</strong> — most applications are processed within 24 to 48 hours.</li>
                    </ul>
                    <p>
                        Verification details are kept private and never shared publicly. Only the verification
                        status (verified or not) is shown on your project page.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}
